/**
 * Checks if the variable is null or undefined
 * @public
 * @type {Function}
 * @param {Mixed} v The variable to test.
 * @return {Boolean}
 */
 const isNullOrUndefined = v => {
  return typeof v === 'undefined' || v === null;
};

/**
 * Checks if the variable is a valid link, null or undefined
 * @public
 * @type {Function}
 * @param {Mixed} l The variable to test.
 * @return {Boolean}
 */
const validLink = l => {
  return !/\b(http|https)/.test(l);
};

/**
 * Checks if the variable is a string.
 * @public
 * @type {Function}
 * @param {String} s The variable to test.
 * @return {Boolean}
 */
const isStr = s => {
  return typeof s === 'string';
};

/**
 * Checks if the variable is an array.
 * @public
 * @type {Function}
 * @param {Array} a The variable to test.
 * @return {Boolean}
 */
const isArr = a => {
  return (
    !isNullOrUndefined(a) &&
    Object.prototype.toString.call(a) === '[object Array]'
  );
};

/**
 * Checks if the variable is a number.
 * @public
 * @type {Function}
 * @param {Number} n The variable to test.
 * @return {Boolean}
 */
const isNum = n => {
  return typeof n === 'number' && !isNaN(n);
};

/**
 * Checks if the variable is an object.
 * @public
 * @type {Function}
 * @param {Object} o The variable to test.
 * @return {Boolean}
 */
const isObj = o => {
  return !isNullOrUndefined(o) && !isArr(o) && typeof o === 'object';
};

/**
 * Checks if the variable is a function.
 * @public
 * @type {Function}
 * @param {Function} f The variable to test.
 * @return {Boolean}
 */
const isFnc = f => {
  return typeof f === 'function';
};

/**
 * Checks if the variable is a string and that it's not empty.
 * @public
 * @type {Function}
 * @param {String} s The variable to test.
 * @return {Boolean}
 */
const validStr = s => {
  return isStr(s) && !!s.length;
};

/**
 * Checks if the variable is an array and that it's not empty. Only goes one level deep.
 * @public
 * @type {Function}
 * @param {Array} a The variable to test.
 * @return {Boolean}
 */
const validArr = a => {
  return isArr(a) && !!a.length;
};

/**
 * Checks if the variable is a number and that is greater than 0.
 * @public
 * @type {Function}
 * @param {Number} n The variable to test.
 * @return {Boolean}
 */
const validNum = n => {
  return isNum(n) && n > 0;
};

/**
 * Checks if the variable is an object and that it's not an empty object.
 * @public
 * @type {Function}
 * @param {Object} o The variable to test.
 * @return {Boolean}
 */
const validObj = o => {
  let i;

  if (isObj(o)) {
    for (i in o) {
      return true;
    }
  }

  return false;
};

/**
 * Checks if the variable is a list of numbers separated by comma
 * Example: 1,2,3
 * @param {String} list list of values to test 
 * @returns {Boolean}
 */
const isListOfNums = list => {
  if (!validStr(list)) return false;

  let valid = true;
  list.split(',').forEach(num => {
    if (!isNum(parseInt(num))) valid = false;
  })

  return valid;
}

const isValidNodeList = nodeList => {
  return NodeList.prototype.isPrototypeOf(nodeList) && nodeList.length > 0
}

export const typeChecker =  {
  isNullOrUndefined,
  validLink,
  isStr,
  isArr,
  isNum,
  isObj,
  isFnc,
  isListOfNums,
  validStr,
  validArr,
  validNum,
  validObj,
  isValidNodeList
};
